import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { CloseButton } from "../input/CloseButton";
import { Modal } from "../modal/Modal";
import { FormattedMessage, useIntl, defineMessages } from "react-intl";
import { NextButton, ContinueButton } from "../input/Button";
import { Button } from "@fluentui/react-components";
import { TextInputField } from "../input/TextInputField";
import { Column } from "../layout/Column";
import { LegalMessage } from "./LegalMessage";

export const SignInStep = {
  submit: "submit",
  waitForVerification: "waitForVerification",
  complete: "complete",
  verify: "verify",
  duplicate: "duplicate"
};

export const SignInMessages = defineMessages({
  pin: {
    id: "sign-in-modal.signin-message.pin",
    defaultMessage: "You'll need to sign in to pin objects."
  },
  unpin: {
    id: "sign-in-modal.signin-message.unpin",
    defaultMessage: "You'll need to sign in to un-pin objects."
  },
  changeScene: {
    id: "sign-in-modal.signin-message.change-scene",
    defaultMessage: "You'll need to sign in to change the scene."
  },
  roomSettings: {
    id: "sign-in-modal.signin-message.room-settings",
    defaultMessage: "You'll need to sign in to change the Verse's settings."
  },
  closeRoom: {
    id: "sign-in-modal.signin-message.close-room",
    defaultMessage: "You'll need to sign in to close the Verse."
  },
  muteUser: {
    id: "sign-in-modal.signin-message.mute-user",
    defaultMessage: "You'll need to sign in to mute other users."
  },
  kickUser: {
    id: "sign-in-modal.signin-message.kick-user",
    defaultMessage: "You'll need to sign in to kick other users."
  },
  addOwner: {
    id: "sign-in-modal.signin-message.add-owner",
    defaultMessage: "You'll need to sign in to assign moderators."
  },
  removeOwner: {
    id: "sign-in-modal.signin-message.remove-owner",
    defaultMessage: "You'll need to sign in to assign moderators."
  },
  createAvatar: {
    id: "sign-in-modal.signin-message.create-avatar",
    defaultMessage: "You'll need to sign in to create avatars."
  },
  remixAvatar: {
    id: "sign-in-modal.signin-message.remix-avatar",
    defaultMessage: "You'll need to sign in to remix avatars."
  },
  remixScene: {
    id: "sign-in-modal.signin-message.remix-scene",
    defaultMessage: "You'll need to sign in to remix scenes."
  },
  favoriteRoom: {
    id: "sign-in-modal.signin-message.favorite-room",
    defaultMessage: "You'll need to sign in to add this room to your favorites."
  },
  favoriteRooms: {
    id: "sign-in-modal.signin-message.favorite-rooms",
    defaultMessage: "You'll need to sign in to add favorite rooms."
  },
  tweet: {
    id: "sign-in-modal.signin-message.tweet",
    defaultMessage: "You'll need to sign in to send tweets."
  },
  mintNft: {
    id: "sign-in-modal.signin-message.mint-nft",
    defaultMessage: "You'll need to sign in to mint NFTs."
  },
  reportBug: {
    id: "sign-in-modal.signin-message.report-bug",
    defaultMessage: "You'll need to sign in to report bugs."
  }
});

const SignInProviders = {
  auth0: "Email and Password",
  "google-oauth2": "Google"
};

export function SubmitEmail({ onSubmitEmail, initialEmail, privacyUrl, termsUrl, message }) {
  const intl = useIntl();

  const [email, setEmail] = useState(initialEmail);

  const onSubmitForm = useCallback(
    e => {
      e.preventDefault();
      onSubmitEmail(email);
    },
    [onSubmitEmail, email]
  );

  const onChangeEmail = useCallback(
    e => {
      setEmail(e.target.value);
    },
    [setEmail]
  );

  return (
    <Column as="form" onSubmit={onSubmitForm}>
      <p>
        {message ? (
          intl.formatMessage(message)
        ) : (
          <FormattedMessage id="sign-in-modal.prompt" defaultMessage="Please Sign In" />
        )}
      </p>
      <TextInputField
        name="email"
        type="email"
        required
        value={email}
        onChange={onChangeEmail}
        placeholder="example@example.com"
      />
      <p>
        <small>
          <LegalMessage termsUrl={termsUrl} privacyUrl={privacyUrl} />
        </small>
      </p>
      <NextButton type="submit" />
    </Column>
  );
}

SubmitEmail.defaultProps = {
  initialEmail: ""
};

SubmitEmail.propTypes = {
  message: PropTypes.object,
  termsUrl: PropTypes.string,
  privacyUrl: PropTypes.string,
  initialEmail: PropTypes.string,
  onSubmitEmail: PropTypes.func.isRequired
};

export function WaitForVerification({ email, onContinue, onCancel, showNewsletterSignup }) {
  const [disabled, setDisabled] = useState(false);

  return (
    <Column>
      <FormattedMessage
        id="sign-in-modal.verifying"
        defaultMessage="<p>A Verification Email was sent to {email}!</p><p>To continue, click on the link in the email using your phone, tablet, or PC.</p><p>No email? Request another below</p>"
        // eslint-disable-next-line react/display-name
        values={{ email, p: chunks => <p>{chunks}</p> }}
      />
      {/* Create a new button to request another email */}
      <div
        style={{
          justifySelf: "right",
          marginLeft: "auto"
        }}
      >
        <Button
          onClick={() => {
            setDisabled(true);
            onContinue();
            setTimeout(() => {
              setDisabled(false);
            }, 10000);
          }}
          appearance="primary"
          disabled={disabled}
        >
          <FormattedMessage id="sign-in-modal.request-another-email" defaultMessage="Request another email" />
        </Button>
      </div>
      {disabled ? (
        <p>
          <FormattedMessage
            id="sign-in-modal.email-requested"
            defaultMessage="Email requested, you can request another in 10 seconds"
          />
        </p>
      ) : null}
    </Column>
  );
}

WaitForVerification.propTypes = {
  showNewsletterSignup: PropTypes.bool,
  email: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired
};

export function SignInComplete({ message, onContinue }) {
  const intl = useIntl();

  return (
    <Column>
      <p>
        <b>
          {message ? (
            intl.formatMessage(message)
          ) : (
            <FormattedMessage id="sign-in-modal.complete" defaultMessage="You are now signed in." />
          )}
        </b>
      </p>
      <ContinueButton onClick={onContinue} />
    </Column>
  );
}

SignInComplete.propTypes = {
  message: PropTypes.string,
  onContinue: PropTypes.func.isRequired
};

export function SignInModal({ closeable, onClose, children, ...rest }) {
  return (
    <Modal
      title={<FormattedMessage id="sign-in-modal.title" defaultMessage="Sign In" />}
      afterTitle={closeable && <CloseButton onClick={onClose} />}
      {...rest}
    >
      {children}
    </Modal>
  );
}

SignInModal.propTypes = {
  closeable: PropTypes.bool,
  onClose: PropTypes.func,
  children: PropTypes.node
};

export function EmailDuplicate({ onClose, onContinue, email, provider }) {
  const [existingProvider, setExistingProvider] = useState();

  useEffect(() => {
    // Get the other provider name
    const otherProviderName = Object.keys(SignInProviders).find(key => key !== provider);

    setExistingProvider(SignInProviders[otherProviderName]);
  }, [provider]);

  return (
    <Column>
      <p>
        <FormattedMessage
          id="sign-in-modal.duplicate"
          defaultMessage="Oops! It looks like the email you're trying to use {email} is already associated with another account. Your previous registration was completed using {provider}."
          values={{ email, provider: existingProvider }}
        />
      </p>
      <Button preset="primary" onClick={onContinue}>
        <FormattedMessage id="sign-in-modal.sign-in-continue" defaultMessage="Try Logging In with provider" />
      </Button>
      <Button onClick={onClose}>
        {/* Continue without sign-in */}
        <FormattedMessage id="sign-in-modal.continue" defaultMessage="Continue without signing in" />
      </Button>
    </Column>
  );
}

EmailDuplicate.propTypes = {
  email: PropTypes.string.isRequired,
  provider: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired
};
